import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, RefreshCw, X, Maximize2, Download, Plus, Key, Settings2, Wand2, AlertCircle, Info, ExternalLink, ShieldCheck, Lock, UserRound, Zap, LogOut, Loader2 } from 'lucide-react';
import { CreativeBrief, CampaignConcept, CampaignImage, AppStep } from './types';
import { generateCampaignConcepts, generateTwinImage, refineBriefText, regenerateSingleThemeConcept } from './services/geminiService';
import { safeStorage } from './services/storage';

const PRODUCTION_LOGS = [
  "Calibrating facial geometry...",
  "Analyzing identity anchors...",
  "Synthesizing volumetric lighting...",
  "Aligning latent identity seeds...",
  "Reconstructing skin shaders...",
  "Balancing color palettes...",
  "Optimizing grain structure...",
  "Finalizing commercial grade..."
];

const ANCHOR_SLOTS = [
  { id: 0, label: "Portrait 1", description: "Direct front view" },
  { id: 1, label: "Portrait 2", description: "Slight angle" },
  { id: 2, label: "Portrait 3", description: "Alternative view" }
];

// Keep to 1 for maximum reliability and to avoid 429 Rate Limits
const MAX_CONCURRENT_RENDERS = 1; 

/**
 * Utility: Compresses image for API compatibility.
 * Identity models only need ~512px to capture features.
 * Lowering this significantly reduces "Payload Too Large" errors.
 */
const compressImage = (file: File, maxWidth = 512, maxHeight = 512): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        // Use JPEG 0.6 for optimal balance between weight and feature clarity (Aggressive compression for safety)
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const SectionHeader: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="text-[10px] font-medium text-white/30 tracking-widest">{number}.</span>
    <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{title}</h3>
  </div>
);

const AssetControl: React.FC<{ 
  label: string; 
  image: string | null; 
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void; 
  onRemove: () => void;
  inputId: string;
  compact?: boolean;
}> = ({ label, image, onUpload, onRemove, inputId, compact }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsProcessing(true);
    try {
      await onUpload(e);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={`flex ${compact ? 'flex-col items-center gap-2 p-3' : 'items-start gap-4 p-4'} bg-white/[0.02] border border-white/5 rounded-xl transition-all hover:bg-white/[0.04]`}>
      <div className={`relative ${compact ? 'w-full aspect-square' : 'w-16 h-16'} bg-white/5 rounded-lg overflow-hidden flex-shrink-0 border border-white/10 group`}>
        {image ? (
          <img src={image} className="w-full h-full object-cover" alt={label} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isProcessing ? <Loader2 className="w-4 h-4 text-white/40 animate-spin" /> : <Camera className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-white/10`} />}
          </div>
        )}
        {image && (
          <button 
            onClick={onRemove}
            className="absolute top-1 right-1 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3 text-white" />
          </button>
        )}
      </div>
      <div className={`${compact ? 'text-center w-full' : 'flex-1 space-y-2'}`}>
        <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 truncate w-full">{label}</p>
        <button 
          disabled={isProcessing}
          onClick={() => document.getElementById(inputId)?.click()}
          className={`w-full py-1 text-[8px] font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-colors text-center disabled:opacity-50 ${compact ? 'mt-1' : ''}`}
        >
          {isProcessing ? 'Processing...' : image ? 'Change' : 'Upload'}
        </button>
        <input type="file" id={inputId} className="hidden" accept="image/*" onChange={handleFileChange} />
      </div>
    </div>
  );
};

const BriefInput: React.FC<{ label: string; value: string; onChange: (v: string) => void; multiline?: boolean; onEnhance?: () => void; isEnhancing?: boolean }> = ({ label, value, onChange, multiline, onEnhance, isEnhancing }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/30">{label}</label>
      {onEnhance && (
        <button onClick={onEnhance} disabled={isEnhancing} className="text-[8px] font-bold uppercase tracking-widest text-white/20 hover:text-white transition-colors flex items-center gap-1">
          <Wand2 className={`w-3 h-3 ${isEnhancing ? 'animate-spin' : ''}`} /> {isEnhancing ? 'Refining...' : 'Refine'}
        </button>
      )}
    </div>
    {multiline ? (
      <textarea 
        value={value} 
        onChange={e => onChange(e.target.value)}
        className="w-full h-24 p-3 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-white/30 transition-colors resize-none leading-relaxed scrollbar-hide"
      />
    ) : (
      <input 
        value={value} 
        onChange={e => onChange(e.target.value)}
        className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-white/30 transition-colors"
      />
    )}
  </div>
);

const SelectInput: React.FC<{ 
  label: string; 
  value: string; 
  options: { value: string; label: string }[]; 
  onChange: (v: any) => void; 
  disabled?: boolean;
  proOnly?: boolean;
}> = ({ label, value, options, onChange, disabled, proOnly }) => (
  <div className={`space-y-1.5 ${disabled ? 'opacity-40' : ''}`}>
    <div className="flex items-center justify-between">
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/30">{label}</label>
      {proOnly && <span className="text-[7px] font-bold text-white/20 uppercase tracking-widest bg-white/5 px-1 rounded">Pro</span>}
    </div>
    <select 
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-[10px] text-white focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value} className="bg-[#0a0a0a]">{opt.label}</option>
      ))}
    </select>
  </div>
);

const ErrorDiagnostic: React.FC<{ error?: string; onSwitchToStandard?: () => void; isPro?: boolean }> = ({ error, onSwitchToStandard, isPro }) => {
  const isSafety = error === "DIAGNOSTIC_SAFETY_BLOCK";
  const isRejected = error === "DIAGNOSTIC_PROMPT_REJECTED";
  const isBilling = error === "DIAGNOSTIC_BILLING_REQUIRED";
  const isQuota = error === "DIAGNOSTIC_QUOTA_EXCEEDED";
  const isSpendLimit = error === "DIAGNOSTIC_SPEND_LIMIT_EXCEEDED";
  const isKey = error === "DIAGNOSTIC_KEY_INVALID";
  const isNetwork = error === "DIAGNOSTIC_SERVER_ERROR";
  const isInput = error === "DIAGNOSTIC_INVALID_INPUT";
  const isHighTraffic = error === "DIAGNOSTIC_HIGH_TRAFFIC";

  let title = "Sync Failure";
  let description = "An unexpected error occurred during synthesis.";
  let action = "Try regenerating this shot.";
  let colorClass = "text-red-500";
  let bgClass = "bg-red-500/10";
  let errorCode = "ERR_UNKNOWN";
  let troubleshootingSteps: string[] = [];

  if (isHighTraffic) {
    title = "High Demand";
    description = "The AI model is temporarily overloaded due to high traffic.";
    action = isPro ? "Try Lite Mode for speed." : "Wait a few seconds and retry.";
    colorClass = "text-amber-500";
    bgClass = "bg-amber-500/10";
    errorCode = "ERR_503_HIGH_TRAFFIC";
    troubleshootingSteps = [
      "Wait 1-2 minutes before trying again.",
      isPro ? "Switch to 'Lite Mode' in settings for faster, more reliable generation." : "Try generating fewer images at once."
    ];
  } else if (isSafety || isRejected) {
    title = isSafety ? "Content Blocked" : "Prompt Rejected";
    description = isSafety 
      ? "Safety filters triggered. Try a simpler directive." 
      : "The synthesis engine rejected this specific combination.";
    action = isPro ? "Try Standard Mode for better permissiveness." : "Soften your creative directive.";
    colorClass = "text-[#dffe00]";
    bgClass = "bg-[#dffe00]/10";
    errorCode = isSafety ? "ERR_400_SAFETY_BLOCK" : "ERR_400_PROMPT_REJECTED";
    troubleshootingSteps = [
      "Remove any potentially sensitive or restricted words from your prompt.",
      "Simplify the creative directive.",
      isPro ? "Switch to Standard Mode which has more permissive filters." : ""
    ].filter(Boolean);
  } else if (isBilling) {
    title = "Account Restricted";
    description = "Studio Rendering requires an active billing project in AI Studio.";
    action = "Visit ai.google.dev/billing";
    errorCode = "ERR_403_BILLING_REQUIRED";
    troubleshootingSteps = [
      "Log into Google AI Studio.",
      "Ensure your project has billing enabled.",
      "Check that your API key is associated with the billed project."
    ];
  } else if (isSpendLimit) {
    title = "Spend Cap Exceeded";
    description = "Your Google AI Studio project has exceeded its monthly spending cap.";
    action = "Adjust spending cap in AI Studio.";
    colorClass = "text-rose-500";
    bgClass = "bg-rose-500/10";
    errorCode = "ERR_429_SPEND_CAP_EXCEEDED";
    troubleshootingSteps = [
      "Navigate to your Spend Management settings.",
      "Log into Google AI Studio at https://ai.studio/spend",
      "Check or increase your project's monthly spending limit to resume rendering."
    ];
  } else if (isQuota) {
    title = "Rate Limited";
    description = "Too many requests. Synthesis is paused briefly.";
    action = "Wait 30 seconds and try again.";
    errorCode = "ERR_429_RATE_LIMITED";
    troubleshootingSteps = [
      "Wait 30-60 seconds before your next request.",
      "If you are using a free tier API key, consider upgrading to a paid tier in Google AI Studio."
    ];
  } else if (isKey) {
    title = "Key Invalid";
    description = "The provided Studio Key is not authorized.";
    action = "Check your key in Studio Settings.";
    errorCode = "ERR_401_INVALID_KEY";
    troubleshootingSteps = [
      "Open the Settings panel (gear icon).",
      "Verify your API key is copied correctly without extra spaces.",
      "Generate a new key in Google AI Studio if the current one was deleted."
    ];
  } else if (isNetwork) {
    title = "Network Error";
    description = "Connection to the AI Studio failed.";
    action = "Check connection and retry.";
    errorCode = "ERR_500_NETWORK_ERROR";
    troubleshootingSteps = [
      "Check your internet connection.",
      "Disable any strict ad-blockers or VPNs that might block API traffic.",
      "Refresh the page."
    ];
  } else if (isInput) {
    title = "Input Error";
    description = "The model rejected the configuration (e.g. invalid aspect ratio).";
    action = "Reset studio parameters.";
    errorCode = "ERR_400_INVALID_INPUT";
    troubleshootingSteps = [
      "Try a different aspect ratio.",
      "Ensure your uploaded images are valid and not corrupted.",
      "Reset all settings to default."
    ];
  } else {
    troubleshootingSteps = [
      "Refresh the page and try again.",
      "Clear your browser cache.",
      "Try a different browser."
    ];
  }

  return (
    <div className="text-center space-y-4 px-6">
      <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${bgClass}`}>
         <AlertCircle className={`w-5 h-5 ${colorClass}`} />
      </div>
      <div className="space-y-1">
        <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white">{title}</span>
        <p className="text-[8px] leading-relaxed text-white/40 uppercase tracking-widest">{description}</p>
        <p className="text-[7px] font-mono text-white/20 mt-2">Code: {errorCode}</p>
      </div>
      
      {troubleshootingSteps.length > 0 && (
        <div className="text-left bg-white/5 p-3 rounded-lg border border-white/10 mt-4">
          <p className="text-[8px] font-bold uppercase tracking-widest text-white/60 mb-2">Troubleshooting Steps:</p>
          <ul className="list-disc pl-4 space-y-1">
            {troubleshootingSteps.map((step, idx) => (
              <li key={idx} className="text-[8px] text-white/50 leading-relaxed">{step}</li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="space-y-2 mt-4">
         {!(isHighTraffic && isPro) && <p className="text-[7px] font-bold text-white/60 uppercase tracking-[0.2em] bg-white/5 py-1 px-2 rounded inline-block">{action}</p>}
         {isSpendLimit && (
            <a 
              href="https://ai.studio/spend" 
              target="_blank" 
              rel="noreferrer" 
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-1.5 mx-auto text-[8px] font-bold uppercase tracking-widest text-[#dffe00] hover:underline bg-[#dffe00]/10 border border-[#dffe00]/20 px-3 py-1.5 rounded-lg transition-all mt-2 w-fit"
            >
              <ExternalLink className="w-2.5 h-2.5" />
              Manage Spending Cap
            </a>
         )}
         {isBilling && (
            <a 
              href="https://ai.google.dev/billing" 
              target="_blank" 
              rel="noreferrer" 
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-1.5 mx-auto text-[8px] font-bold uppercase tracking-widest text-[#dffe00] hover:underline bg-[#dffe00]/10 border border-[#dffe00]/20 px-3 py-1.5 rounded-lg transition-all mt-2 w-fit"
            >
              <ExternalLink className="w-2.5 h-2.5" />
              Visit Billing Docs
            </a>
         )}
         {isPro && onSwitchToStandard && (
            <button 
              onClick={(e) => { e.stopPropagation(); onSwitchToStandard(); }}
              className={`flex items-center gap-1.5 mx-auto text-[7px] font-bold uppercase tracking-widest ${isHighTraffic ? 'text-amber-500 hover:text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/20' : 'text-emerald-500 hover:text-emerald-400'} transition-colors mt-1`}
            >
              <Zap className="w-2.5 h-2.5" />
              {isHighTraffic ? 'Switch to Lite Mode (Reliable)' : 'Try Standard Rendering'}
            </button>
         )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('setup');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [hasKey, setHasKey] = useState(false);
  const [inputKey, setInputKey] = useState('');
  const [showKeyUpdate, setShowKeyUpdate] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isRefining, setIsRefining] = useState(false);
  const [currentLogIdx, setCurrentLogIdx] = useState(0);
  const [themeRefreshing, setThemeRefreshing] = useState<Record<number, boolean>>({});
  const [handshakeStatus, setHandshakeStatus] = useState("Establishing secure platform handshake...");

  const [brief, setBrief] = useState<CreativeBrief>(() => {
    try {
      const saved = safeStorage.getItem('twin-studio-brief-v25');
      return saved ? JSON.parse(saved) : {
        creativeDirection: 'Editorial lifestyle shoot for a luxury brand with a soft-glam vibe.',
        environment: 'Upscale minimalist interiors, bright natural light.',
        lighting: '5600K daylight with soft falloff',
        camera: '85mm portrait compression',
        texture: 'organic cotton, polished stone',
        palette: 'Neutral tones, cream, black, sage green',
        moodRefs: 'Vogue editorial',
        bodyType: 'Balanced',
        realismPrompt: 'High-end aesthetic, clean studio lighting, 8k resolution.',
        negativePrompt: 'collage, split screen, text, watermark, blurry, distorted anatomy',
        anchorImages: [null, null, null],
        outfits: [null, null, null, null],
        proMode: false,
        aspectRatio: "3:4",
        imageSize: "1K",
        variationStrength: 'medium',
        sensor: 'Sony A7R V',
        lightingTopology: 'Studio Softbox',
        aperture: '1.8',
        rawTexture: true
      };
    } catch (e) {
      return {
        creativeDirection: 'Editorial lifestyle shoot for a luxury brand with a soft-glam vibe.',
        environment: 'Upscale minimalist interiors, bright natural light.',
        lighting: '5600K daylight with soft falloff',
        camera: '85mm portrait compression',
        texture: 'organic cotton, polished stone',
        palette: 'Neutral tones, cream, black, sage green',
        moodRefs: 'Vogue editorial',
        bodyType: 'Balanced',
        realismPrompt: 'High-end aesthetic, clean studio lighting, 8k resolution.',
        negativePrompt: 'collage, split screen, text, watermark, blurry, distorted anatomy',
        anchorImages: [null, null, null],
        outfits: [null, null, null, null],
        proMode: false,
        aspectRatio: "3:4",
        imageSize: "1K",
        variationStrength: 'medium',
        sensor: 'Sony A7R V',
        lightingTopology: 'Studio Softbox',
        aperture: '1.8',
        rawTexture: true
      };
    }
  });

  const [concepts, setConcepts] = useState<CampaignConcept[]>([]);
  const [images, setImages] = useState<CampaignImage[]>([]);

  const briefRef = useRef(brief);
  const conceptsRef = useRef(concepts);
  const proRef = useRef(isPro);
  const renderQueueRef = useRef<(() => Promise<void>)[]>([]);
  const activeCountRef = useRef(0);

  const totalImages = images.length;
  const completedImages = images.filter(img => img.status === 'completed').length;
  const failedImages = images.filter(img => img.status === 'failed').length;
  const inProgress = totalImages > 0 && completedImages + failedImages < totalImages;

  useEffect(() => { 
    briefRef.current = brief; 
    try {
      safeStorage.setItem('twin-studio-brief-v25', JSON.stringify(brief)); 
    } catch (e) {
      console.warn("Storage quota hit.");
    }
  }, [brief]);

  useEffect(() => { conceptsRef.current = concepts; }, [concepts]);
  useEffect(() => { proRef.current = isPro; }, [isPro]);

  useEffect(() => {
    if (isGenerating || inProgress) {
      const interval = setInterval(() => {
        setCurrentLogIdx(prev => (prev + 1) % PRODUCTION_LOGS.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isGenerating, inProgress]);

  useEffect(() => {
    const checkKey = async () => {
      const localKey = safeStorage.getItem('STUDIO_USER_API_KEY') || '';
      const maxRetries = 15;
      let attempt = 0;

      while (attempt < maxRetries) {
        attempt++;
        if (attempt > 1) {
          setHandshakeStatus(`Waking up rendering cluster (Attempt ${attempt}/${maxRetries})...`);
        } else {
          setHandshakeStatus("Verifying Studio environment credentials...");
        }

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          const res = await fetch("/api/has-key", {
            headers: {
              'x-api-key': localKey
            },
            signal: controller.signal
          }).catch(() => null);

          clearTimeout(timeoutId);

          if (res && res.ok) {
            const data = await res.json().catch(() => null);
            if (data && data.hasKey) {
              setHasKey(true);
              setIsInitialized(true);
              return;
            }
            // If the server explicitly responded but hasKey is false, it means neither the server
            // has process.env.GEMINI_API_KEY nor the visitor has a custom key.
            // Since there is no key anywhere, break out of retry loop early to show the key entry UI.
            break;
          }
        } catch (err) {
          console.warn(`Handshake query failed (Attempt ${attempt}/${maxRetries}):`, err);
        }

        // Wait 2.5 seconds before retrying
        await new Promise(resolve => setTimeout(resolve, 2500));
      }

      // Check client-side secure store fallback as final step
      const finalLocalKey = safeStorage.getItem('STUDIO_USER_API_KEY');
      setHasKey(!!finalLocalKey);
      setIsInitialized(true);
    };
    checkKey();
  }, []);

  const handleKeySave = async () => {
    try {
      if (inputKey.trim().length > 20) {
        safeStorage.setItem('STUDIO_USER_API_KEY', inputKey.trim());
        setHasKey(true);
      } else {
        alert("Please paste a valid Gemini API Key.");
      }
    } catch (e) {
      console.error("Failed to save credentials", e);
    }
  };

  const handleRevokeKey = () => {
    safeStorage.removeItem('STUDIO_USER_API_KEY');
    setHasKey(false);
  };

  const processQueue = useCallback(async () => {
    if (activeCountRef.current >= MAX_CONCURRENT_RENDERS || renderQueueRef.current.length === 0) return;
    activeCountRef.current++;
    const nextRender = renderQueueRef.current.shift();
    if (nextRender) await nextRender();
    activeCountRef.current--;
    processQueue();
  }, []);

  const handleRefine = async () => {
    if (!brief.creativeDirection) return;
    setIsRefining(true);
    try {
      const refined = await refineBriefText(brief.creativeDirection);
      setBrief(prev => ({ ...prev, creativeDirection: refined }));
    } catch (err) {
      console.error(err);
    } finally { setIsRefining(false); }
  };

  // Modified render task to handle lite mode override and status updates
  const renderImageTask = useCallback(async (imgId: string, themeIdx: number, overrideConcepts?: CampaignConcept[], forceLiteMode = false) => {
    const activeConcepts = overrideConcepts || conceptsRef.current;
    const currentConcept = activeConcepts.find(c => c.themeIndex === themeIdx);
    const currentBrief = briefRef.current;
    if (!currentConcept) return;

    setImages(prev => prev.map(img => img.id === imgId ? { ...img, status: 'generating', error: undefined, progressLog: 'Initializing...' } : img));
    
    // Status callback to update UI during retries
    const updateProgress = (msg: string) => {
       setImages(prev => prev.map(img => img.id === imgId ? { ...img, progressLog: msg } : img));
    };

    try {
      const usePro = forceLiteMode ? false : proRef.current;
      
      const url = await generateTwinImage(
        currentBrief, 
        currentBrief.outfits[themeIdx - 1], 
        currentConcept.prompt, 
        undefined, 
        currentBrief.variationStrength, 
        usePro,
        updateProgress // Pass callback
      );

      setImages(prev => prev.map(img => img.id === imgId ? { 
        ...img, 
        status: 'completed', 
        imageUrl: url, 
        metadata: { sensor: currentBrief.sensor, aperture: currentBrief.aperture, lighting: currentBrief.lightingTopology } 
      } : img));

    } catch (err: any) {
      if (err.message === "API_KEY_MISSING") {
         setHasKey(false);
         return;
      }
      setImages(prev => prev.map(img => img.id === imgId ? { ...img, status: 'failed', error: err.message } : img));
    }
  }, []);

  const queueRender = (imgId: string, themeIdx: number, overrideConcepts?: CampaignConcept[], forceLiteMode = false) => {
    setImages(prev => prev.map(img => img.id === imgId ? { ...img, status: 'queued', error: undefined } : img));
    renderQueueRef.current.push(() => renderImageTask(imgId, themeIdx, overrideConcepts, forceLiteMode));
    processQueue();
  };

  const handleRegenerateTheme = async (themeIndex: number) => {
    setThemeRefreshing(prev => ({ ...prev, [themeIndex]: true }));
    try {
      const newConcept = await regenerateSingleThemeConcept(brief, themeIndex);
      setConcepts(prev => prev.map(c => c.themeIndex === themeIndex ? newConcept : c));
      const updatedConcepts = concepts.map(c => c.themeIndex === themeIndex ? newConcept : c);
      const themeImages = images.filter(img => img.themeIndex === themeIndex);
      themeImages.forEach(img => {
        queueRender(img.id, themeIndex, updatedConcepts);
      });
      processQueue();
    } catch (err) {
      console.error("Theme regeneration failed", err);
    } finally {
      setThemeRefreshing(prev => ({ ...prev, [themeIndex]: false }));
    }
  };

  const generateCampaign = async () => {
    setIsGenerating(true);
    try {
      const generatedConcepts = await generateCampaignConcepts(brief);
      setConcepts(generatedConcepts);
      const initialImages: CampaignImage[] = [];
      generatedConcepts.forEach(c => {
        for (let i = 0; i < 4; i++) initialImages.push({ id: `${c.id}-${i}`, themeIndex: c.themeIndex, status: 'idle' });
      });
      setImages(initialImages);
      setStep('studio');
      initialImages.forEach(img => queueRender(img.id, img.themeIndex, generatedConcepts));
    } catch (err: any) {
      if (err.message === "API_KEY_MISSING" || err.message === "DIAGNOSTIC_KEY_INVALID") {
        setHasKey(false);
        setShowKeyUpdate(true);
      } else if (err.message === "DIAGNOSTIC_SPEND_LIMIT_EXCEEDED") {
        alert("Monthly Spending Cap Exceeded:\nYour Google AI Studio project has hit its monthly spending cap. Please go to https://ai.studio/spend to manage your project spend cap.");
      } else if (err.message === "DIAGNOSTIC_QUOTA_EXCEEDED") {
        alert("Rate Limit Exceeded:\nToo many requests. Please wait briefly and try again.");
      } else if (err.message === "DIAGNOSTIC_BILLING_REQUIRED") {
        alert("Billing Required:\nYour API key requires billing to be enabled or updated in Google AI Studio.");
        setShowKeyUpdate(true);
      } else {
        alert(`Notice during concept generation: ${err.message || err}`);
      }
    } finally { setIsGenerating(false); }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-6 animate-fade-in font-sans">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="w-12 h-12 rounded-full border-t-2 border-[#dffe00] border-r-2 border-r-transparent animate-spin mx-auto"></div>
          <div className="space-y-3">
            <h2 className="font-serif text-2xl font-semibold tracking-wide">AI Twin Studio</h2>
            <p className="text-[10px] text-white/50 uppercase tracking-[0.25em] font-mono animate-pulse">{handshakeStatus}</p>
          </div>
          <p className="text-[9px] text-white/20 leading-relaxed">
            If the network handshake takes more than a moment due to cold-starts or sandboxed browsing, you can bypass the check to connect your access key immediately.
          </p>
          <button 
            onClick={() => {
              setHasKey(false);
              setIsInitialized(true);
            }}
            className="px-6 py-3.5 bg-white/5 border border-white/10 rounded-xl text-[9px] font-bold uppercase tracking-widest text-[#dffe00] hover:text-white transition-all hover:bg-[#dffe00]/15"
          >
            Bypass to Key Setup
          </button>
        </div>
      </div>
    );
  }

  if (!hasKey) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6 animate-fade-in font-sans">
        <div className="max-w-md w-full space-y-12 text-center">
           <div className="space-y-4">
              <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-3xl mx-auto flex items-center justify-center">
                 <Lock className="w-6 h-6 text-white/40" />
              </div>
              <h1 className="font-serif text-4xl font-semibold tracking-[1px]">AI Twin Studio: <span className="text-[#dffe00]">Hyper Realism</span></h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">Connect Studio Access Key to Begin</p>
           </div>
           <div className="space-y-4">
              <input 
                type="password" 
                placeholder="Paste Gemini API Key" 
                value={inputKey} 
                onChange={(e) => setInputKey(e.target.value)} 
                className="w-full py-5 px-6 bg-white/5 border border-white/10 rounded-2xl text-center text-white focus:outline-none focus:border-white/30 transition-all placeholder:text-white/10 text-sm tracking-widest font-mono" 
              />
              <button onClick={handleKeySave} className="w-full py-6 bg-white text-black text-[11px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:bg-[#dffe00] transition-all active:scale-[0.98]">
                Activate Studio App
              </button>
              <p className="text-[10px] text-white/30 mt-2">
                Paste your Gemini API Key in the field above to connect.
              </p>
           </div>
           <div className="pt-8 border-t border-white/5">
              <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noreferrer" className="text-[9px] uppercase tracking-widest text-white/30 hover:text-white flex items-center justify-center gap-2 transition-colors">Billing Documentation <ExternalLink className="w-3 h-3" /></a>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-white selection:text-black animate-fade-in overflow-x-hidden">
      {previewImage && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-8 backdrop-blur-xl" onClick={() => setPreviewImage(null)}>
           <button onClick={() => setPreviewImage(null)} className="absolute top-10 right-10 text-white/50 hover:text-white transition-colors"><X className="w-8 h-8" /></button>
           <img src={previewImage} className="max-w-full max-h-[85vh] object-contain rounded-lg border border-white/10" alt="Preview" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {showKeyUpdate && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-6 backdrop-blur-md animate-fade-in">
           <div className="max-w-md w-full bg-[#0c0c0c] border border-white/10 rounded-2xl p-8 space-y-6 relative shadow-2xl">
              <button onClick={() => setShowKeyUpdate(false)} className="absolute top-4 right-4 text-white/20 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
              
              <div className="space-y-2 text-center">
                 <div className="w-12 h-12 bg-white/5 rounded-xl mx-auto flex items-center justify-center mb-4">
                    <Key className="w-6 h-6 text-[#dffe00]" />
                 </div>
                 <h3 className="font-serif text-2xl">Studio Access Key</h3>
                 <p className="text-[10px] uppercase tracking-widest text-white/40">
                   Update your Gemini API Key below
                 </p>
              </div>

              <div className="space-y-4">
                 <input 
                    type="password" 
                    placeholder="Paste new API Key" 
                    value={inputKey} 
                    onChange={(e) => setInputKey(e.target.value)} 
                    className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white text-center focus:outline-none focus:border-[#dffe00]/50 transition-colors text-xs tracking-widest"
                 />
                 <button 
                    onClick={() => { handleKeySave(); setShowKeyUpdate(false); }} 
                    className="w-full py-4 bg-white text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-[#dffe00] transition-colors"
                 >
                    Save & Connect
                 </button>
                 <button 
                    onClick={() => { handleRevokeKey(); setShowKeyUpdate(false); }}
                    className="w-full py-3 bg-red-500/10 text-red-500 border border-red-500/20 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-red-500/20 transition-colors"
                 >
                    Revoke Key
                 </button>
              </div>
              
              <div className="text-center pt-4 border-t border-white/5">
                 <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noreferrer" className="text-[9px] uppercase tracking-widest text-white/20 hover:text-white transition-colors inline-flex items-center gap-2">
                    Billing Documentation <ExternalLink className="w-3 h-3" />
                 </a>
              </div>
           </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <aside className="w-full md:w-[480px] border-b md:border-b-0 md:border-r border-white/5 bg-[#0a0a0a] flex flex-col md:h-full">
          <div className="flex-1 md:overflow-y-auto scrollbar-hide p-6 md:p-10 space-y-12">
            <header className="text-center pb-8 border-b border-white/5">
              <h1 className="font-serif text-3xl font-semibold tracking-[1px]">AI Twin Studio: <span className="text-[#dffe00]">Hyper Realism</span></h1>
            </header>

            <div className="space-y-10">
              <section className="space-y-6">
                <SectionHeader number="01" title="Identity Vault" />
                <div className="grid grid-cols-3 gap-3">
                  {ANCHOR_SLOTS.map((slot) => (
                    <AssetControl key={slot.id} compact label={slot.label} image={brief.anchorImages[slot.id]} onUpload={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const compressed = await compressImage(file);
                            setBrief(prev => {
                              const newAnchors = [...prev.anchorImages];
                              newAnchors[slot.id] = compressed;
                              return { ...prev, anchorImages: newAnchors };
                            });
                          } catch (err) { alert("Failed to process image."); }
                        }
                      }} onRemove={() => setBrief(prev => {
                        const newAnchors = [...prev.anchorImages];
                        newAnchors[slot.id] = null;
                        return { ...prev, anchorImages: newAnchors };
                      })} inputId={`anchor-${slot.id}`} />
                  ))}
                </div>
                <div className="p-6 bg-[#0d0d09] border border-[#dffe00]/20 rounded-2xl space-y-4">
                   <div className="flex items-center gap-3 text-[#dffe00]">
                      <UserRound className="w-4 h-4" />
                      <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Anatomical Archetype</span>
                   </div>
                   <SelectInput label="Body Type" value={brief.bodyType} options={[
                        { value: "Slim", label: "Slim" }, { value: "Athletic", label: "Athletic" }, { value: "Curvy", label: "Curvy" }, { value: "Petite", label: "Petite" }, { value: "Tall", label: "Tall" }, { value: "Balanced", label: "Balanced" }, { value: "Large", label: "Large" }
                      ]} onChange={v => setBrief(p => ({...p, bodyType: v}))} />
                </div>
              </section>

              <section className="space-y-6 pt-4 border-t border-white/5">
                <SectionHeader number="02" title="Optics & Texture" />
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl space-y-6 shadow-xl">
                   <div className="grid grid-cols-2 gap-4">
                      <SelectInput label="Camera" value={brief.sensor} options={[
                        { value: "Sony A7R V", label: "Sony A7R V (Sharp)" },
                        { value: "Canon EOS R5", label: "Canon EOS R5 (Natural)" },
                        { value: "Leica M11", label: "Leica M11 (Artistic)" },
                        { value: "Hasselblad X2D", label: "Hasselblad (Studio)" },
                        { value: "Fujifilm GFX", label: "Fujifilm (Rich)" },
                        { value: "Phase One IQ4", label: "Phase One (Elite)" },
                        { value: "35mm Film", label: "Analog Film (Grain)" },
                        { value: "Polaroid I-2", label: "Instant (Vintage)" }
                      ]} onChange={v => setBrief(p => ({...p, sensor: v}))} />
                      <SelectInput label="Lighting Mode" value={brief.lightingTopology} options={[
                        { value: "Studio Softbox", label: "Softbox (Clean)" },
                        { value: "Rembrandt", label: "Rembrandt (Moody)" },
                        { value: "Butterfly", label: "Butterfly (Fashion)" },
                        { value: "Golden Hour", label: "Golden Hour (Warm)" },
                        { value: "High-Key", label: "High-Key (Bright)" },
                        { value: "Cyberpunk Neon", label: "Cyberpunk (Vivid)" },
                        { value: "Moody Cinematic", label: "Cinematic (Haze)" },
                        { value: "Ring Light", label: "Ring Light (Beauty)" },
                        { value: "Rim Light", label: "Rim Light (Contour)" },
                        { value: "Candlelight", label: "Candlelight (Intimate)" }
                      ]} onChange={v => setBrief(p => ({...p, lightingTopology: v}))} />
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                      <SelectInput label="Aperture (DoF)" value={brief.aperture} options={[
                        { value: "1.2", label: "f/1.2 (Extreme Bokeh)" },
                        { value: "1.4", label: "f/1.4 (Ultra Soft)" },
                        { value: "1.8", label: "f/1.8 (Creamy)" },
                        { value: "2.8", label: "f/2.8 (Fashion Std)" },
                        { value: "4.0", label: "f/4.0 (Portrait Sharp)" },
                        { value: "5.6", label: "f/5.6 (Sharp Detail)" },
                        { value: "8.0", label: "f/8.0 (Deep Focus)" },
                        { value: "11", label: "f/11 (Max Depth)" },
                        { value: "22", label: "f/22 (Studio Still)" }
                      ]} onChange={v => setBrief(p => ({...p, aperture: v}))} />
                      <div className="flex flex-col gap-1.5">
                         <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/30">Raw Texture</label>
                         <button 
                            onClick={() => setBrief(p => ({...p, rawTexture: !p.rawTexture}))}
                            className={`w-full h-9 rounded-lg border flex items-center justify-center gap-2 transition-all ${brief.rawTexture ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' : 'bg-white/5 border-white/10 text-white/20'}`}
                         >
                            <Zap className="w-3.5 h-3.5" />
                            <span className="text-[9px] font-bold uppercase tracking-widest">{brief.rawTexture ? 'Active' : 'Disabled'}</span>
                         </button>
                      </div>
                   </div>
                </div>
              </section>

              <section className="space-y-6 pt-4 border-t border-white/5">
                <SectionHeader number="03" title="Creative Direction" />
                <div className="space-y-6">
                  <BriefInput label="Directive" value={brief.creativeDirection} onChange={v => setBrief(p => ({...p, creativeDirection: v}))} multiline onEnhance={handleRefine} isEnhancing={isRefining} />
                  <div className="grid grid-cols-2 gap-4">
                    <BriefInput label="Environment" value={brief.environment} onChange={v => setBrief(p => ({...p, environment: v}))} />
                    <BriefInput label="Lighting" value={brief.lighting} onChange={v => setBrief(p => ({...p, lighting: v}))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <BriefInput label="Camera Focus" value={brief.camera} onChange={v => setBrief(p => ({...p, camera: v}))} />
                    <BriefInput label="Texture/Mood" value={brief.texture} onChange={v => setBrief(p => ({...p, texture: v}))} />
                  </div>
                </div>
              </section>

              <section className="space-y-6 pt-4 border-t border-white/5">
                <SectionHeader number="04" title="Wardrobe Capsule" />
                <div className="grid grid-cols-2 gap-4">
                  {brief.outfits.map((o, i) => (
                    <AssetControl key={i} label={`Look ${i + 1}`} image={o} onUpload={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const compressed = await compressImage(file);
                          setBrief(prev => {
                            const newOutfits = [...prev.outfits];
                            newOutfits[i] = compressed;
                            return { ...prev, outfits: newOutfits };
                          });
                        } catch (err) { alert("Failed to process image."); }
                      }
                    }} onRemove={() => setBrief(prev => {
                      const newOutfits = [...prev.outfits];
                      newOutfits[i] = null;
                      return { ...prev, outfits: newOutfits };
                    })} inputId={`wardrobe-${i}`} />
                  ))}
                </div>
              </section>

              <section className="space-y-6 pt-4 border-t border-white/5">
                <SectionHeader number="05" title="Studio Parameters" />
                <div className="grid grid-cols-3 gap-4">
                  <SelectInput label="Ratio" value={brief.aspectRatio} options={[
                      { value: "1:1", label: "1:1" }, { value: "3:4", label: "3:4" }, { value: "4:5", label: "4:5" }, { value: "4:3", label: "4:3" }, { value: "9:16", label: "9:16" }, { value: "16:9", label: "16:9" }
                    ]} onChange={v => setBrief(p => ({...p, aspectRatio: v}))} />
                  <SelectInput label="Res" value={brief.imageSize} options={[
                      { value: "1K", label: "1K" }, { value: "2K", label: "2K" }, { value: "4K", label: "4K" }
                    ]} onChange={v => setBrief(p => ({...p, imageSize: v}))} />
                  <SelectInput label="Likeness" value={brief.variationStrength} options={[{ value: "low", label: "Soft" }, { value: "medium", label: "Mid" }, { value: "high", label: "Strict" }]} onChange={v => setBrief(p => ({...p, variationStrength: v}))} />
                </div>
              </section>

              <section className="space-y-6 pt-12 pb-12 border-t border-white/5">
                <div className="flex items-center gap-2 mb-4">
                  <Settings2 className="w-3.5 h-3.5 text-white/20" />
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Studio Settings</h3>
                </div>
                <div className="p-4 bg-white/[0.02] border border-white/10 rounded-xl flex items-center justify-between">
                   <div className="flex items-center gap-3">
                      <ShieldCheck className="w-4 h-4 text-emerald-500/50" />
                      <div className="flex flex-col">
                         <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">Studio Access</span>
                         <span className="text-[8px] uppercase tracking-widest text-emerald-500">Connected</span>
                      </div>
                   </div>
                   <button onClick={handleRevokeKey} className="p-2 hover:bg-red-500/10 rounded-lg text-white/20 hover:text-red-500 transition-all"><LogOut className="w-3.5 h-3.5" /></button>
                </div>
                
                <div className="space-y-2">
                   <div className="px-1 flex items-center gap-2 relative">
                      <input type="checkbox" id="pro-mode" checked={isPro} onChange={e => setIsPro(e.target.checked)} className="w-3 h-3 rounded bg-white/5 border border-white/20 cursor-pointer" />
                      <label htmlFor="pro-mode" className="text-[9px] font-bold uppercase tracking-widest text-white/40 cursor-pointer select-none">Enable Pro Rendering</label>
                      <div className="group relative">
                        <Info className="w-3 h-3 text-white/20 cursor-help" />
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-56 p-3 bg-[#151515] border border-white/10 rounded-xl shadow-xl backdrop-blur-md hidden group-hover:block z-50">
                          <p className="text-[9px] leading-relaxed text-white/60">
                            Switches to <span className="text-white font-bold">Gemini 3.0 Pro</span>. Enables 4K output, advanced lighting physics, and complex instruction following. <span className="text-[#dffe00]">Requires Billing</span>.
                          </p>
                        </div>
                      </div>
                   </div>
                   {isPro && (
                     <div className="px-1 pl-6">
                       <p className="text-[8px] uppercase tracking-widest text-[#dffe00]/60 flex items-center gap-2">
                          <Zap className="w-2.5 h-2.5" /> High-Fidelity Model Active
                       </p>
                     </div>
                   )}
                </div>

              </section>
            </div>

            <div className="pt-6 pb-20">
              {!isGenerating && !inProgress ? (
                <button onClick={generateCampaign} className="w-full py-6 text-[11px] font-bold uppercase tracking-[0.3em] rounded-2xl bg-white text-black hover:bg-[#dffe00] transition-all">Produce Brand Capsule</button>
              ) : (
                <div className="w-full h-20 bg-[#dffe00] rounded-2xl flex items-center px-6 relative overflow-hidden border border-black/5">
                   <div className="absolute left-0 bottom-0 h-1 bg-black/10 transition-all duration-1000" style={{ width: `${(completedImages / totalImages) * 100 || 10}%` }} />
                   <div className="flex items-center gap-4 w-full z-10">
                      <RefreshCw className="w-5 h-5 text-black animate-spin" />
                      <div className="flex-1">
                         <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black block">Studio Rendering</span>
                         <p className="text-[8px] uppercase tracking-widest text-black/40 font-medium truncate">{PRODUCTION_LOGS[currentLogIdx]}</p>
                      </div>
                   </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-white/5 bg-[#0a0a0a] flex items-center justify-between z-10">
             <div className="flex flex-col">
                <div className="flex items-center gap-2">
                   <span className="text-[10px] font-bold uppercase tracking-widest text-white">AI Twin Studio</span>
                   {isPro && <div className="w-1.5 h-1.5 rounded-full bg-[#dffe00] animate-pulse" title="Pro Mode Active" />}
                </div>
                <span className="text-[8px] uppercase tracking-widest text-white/30">v1.2.5 • Gemini 3.0</span>
             </div>
             <button onClick={() => setShowKeyUpdate(true)} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all group">
                <Key className="w-3.5 h-3.5 text-white/40 group-hover:text-[#dffe00] transition-colors" />
                <span className="text-[9px] font-bold uppercase tracking-widest text-white/60 group-hover:text-white transition-colors">Keys</span>
             </button>
          </div>
        </aside>

        <main className="flex-1 w-full bg-[#0a0a0a] flex flex-col items-center md:h-full md:overflow-y-auto scrollbar-hide">
          <header className="w-full pt-16 md:pt-32 pb-12 md:pb-24 text-center max-w-3xl px-6 md:px-12">
            <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-[1px] text-white/95">Synthetic Pro</h2>
            <p className="text-[9px] md:text-[12px] uppercase tracking-[0.4em] text-white/40 mt-6">Hyper-Realistic Multi-Anchor Campaign production</p>
          </header>

          <div className="w-full max-w-7xl px-6 md:px-12 pb-24 md:pb-48 space-y-24 md:space-y-48">
            {step === 'setup' ? (
              <div className="h-[20vh] md:h-[30vh] flex flex-col items-center justify-center text-center space-y-6">
                <Plus className="w-6 h-6 text-white/10" />
                <p className="text-[10px] uppercase tracking-[0.5em] text-white/20">Awaiting Directive</p>
              </div>
            ) : (
              concepts.map((concept) => (
                <section key={concept.id} className="space-y-8 md:space-y-12">
                  <header className="border-b border-white/10 pb-8 md:pb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3 md:mb-4">
                        <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.5em] text-white/30">Theme 0{concept.themeIndex}</span>
                        <button onClick={() => handleRegenerateTheme(concept.themeIndex)} disabled={themeRefreshing[concept.themeIndex] || inProgress} className="flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-[8px] font-bold uppercase tracking-widest hover:bg-white/10 transition-all disabled:opacity-30 group">
                          <RefreshCw className={`w-2.5 h-2.5 ${themeRefreshing[concept.themeIndex] ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                          {themeRefreshing[concept.themeIndex] ? 'Regenerating...' : 'Regenerate Theme'}
                        </button>
                      </div>
                      <h3 className="font-serif text-2xl md:text-5xl font-semibold tracking-[1px]">{concept.title}</h3>
                      <p className="text-[10px] md:text-[11px] text-white/40 uppercase tracking-widest mt-4 max-w-2xl">{concept.description}</p>
                    </div>
                  </header>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {images.filter(img => img.themeIndex === concept.themeIndex).map((img) => (
                      <div key={img.id} className="relative aspect-square bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group shadow-2xl">
                        {img.status === 'completed' && img.imageUrl ? (
                          <>
                            <img src={img.imageUrl} className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110" alt="render" />
                            
                            {/* EXIF Metadata Overlay */}
                            <div className="absolute top-4 left-4 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                               <div className="px-2 py-0.5 bg-black/60 backdrop-blur-md rounded border border-white/10 text-[6px] font-bold uppercase tracking-[0.2em] text-white/80 w-fit">{img.metadata?.sensor}</div>
                               <div className="px-2 py-0.5 bg-black/60 backdrop-blur-md rounded border border-white/10 text-[6px] font-bold uppercase tracking-[0.2em] text-white/80 w-fit">f/{img.metadata?.aperture} ISO 100</div>
                               <div className="px-2 py-0.5 bg-black/60 backdrop-blur-md rounded border border-white/10 text-[6px] font-bold uppercase tracking-[0.2em] text-white/80 w-fit">{img.metadata?.lighting}</div>
                            </div>

                            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3">
                              <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-3 bg-white text-black rounded-full hover:scale-110 transition-transform shadow-lg"><Maximize2 className="w-4 h-4 md:w-5 h-5" /></button>
                              <button onClick={() => {
                                const link = document.createElement('a');
                                link.href = img.imageUrl!;
                                link.download = `render-${img.id}.png`;
                                link.click();
                              }} className="p-3 bg-white text-black rounded-full hover:scale-110 transition-transform shadow-lg"><Download className="w-4 h-4 md:w-5 h-5" /></button>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-[#0c0c0c]">
                            {img.status === 'failed' ? (
                              <div className="space-y-6">
                                <ErrorDiagnostic 
                                  error={img.error} 
                                  isPro={isPro} 
                                  onSwitchToStandard={() => { 
                                    // If failing due to High Traffic, force lite mode for this specific retry
                                    const isHighTraffic = img.error === "DIAGNOSTIC_HIGH_TRAFFIC";
                                    if (isHighTraffic) {
                                      queueRender(img.id, img.themeIndex, undefined, true); // forceLiteMode = true
                                    } else {
                                      setIsPro(false); 
                                      queueRender(img.id, img.themeIndex); 
                                    }
                                  }} 
                                />
                                <div className="flex flex-col items-center gap-2">
                                  <button onClick={() => queueRender(img.id, img.themeIndex)} className="text-[8px] font-bold uppercase tracking-widest text-white/40 hover:text-white underline transition-colors">Retry Shot</button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-4 text-center">
                                <div className={`w-8 h-8 mx-auto border-t-2 border-white/20 rounded-full ${img.status === 'idle' ? '' : 'animate-spin'}`} />
                                <div>
                                  <span className="block text-[9px] uppercase tracking-[0.3em] text-white/40">
                                    {img.status === 'queued' ? 'In Queue' : img.progressLog || 'Synthesizing'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;